import * as Sentry from '@sentry/react'
import Papa from 'papaparse'
import type { DraftState } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { hasKeyColumn } from './sheet/sheetMapping.ts'
import { sheetMatchOf } from './sheet/sheetMatching.ts'
import { LOW_MATCH_SHARE } from './primerPaste.ts'

/*
 * Relay (our tunnel) accepts 200 MiB per attachment by default; a sample sheet is kilobytes, so anything
 * past this is trimmed to its first rows rather than uploaded whole from the browser.
 */
export const MAX_SHEET_ATTACHMENT_BYTES = 1_000_000

export type SupportReason = 'parse-failed' | 'no-key-column' | 'low-match'

export type SupportStats = { reason: SupportReason; rows: number; matched: number }

/** What the lab may send: the sheet as dropped (or the pasted text), with what the wizard made of it. */
export type SupportRequest = {
  name: string
  source: { kind: 'file'; file: File } | { kind: 'text'; text: string }
  /** The sheet as read, for trimming a file too large to send whole. */
  table?: { headers: string[]; rows: Record<string, string>[] }
  columns: { headers: string[]; mapped?: Record<string, string> }
  stats: SupportStats
  /** The order's FASTQ file names, and nothing else of the order. */
  fileNames: string[]
}

export type SupportTrigger = { fileKey: string; name: string; stats: SupportStats }

/** Sending needs a configured Sentry; local development has none. */
export const supportAvailable = (): boolean => !!Sentry.getClient()?.getDsn()

export const fastqNamesOf = (draft: DraftState): string[] =>
  draft.fileOrder.map((key) => draft.files[key]).flatMap((file) => (file?.kind === 'fastq' ? [file.name] : []))

/** A dropped sheet the wizard could not use: unreadable, without a column naming the samples, or matching few of its rows. */
export const sheetSupportTrigger = (draft: DraftState): SupportTrigger | undefined => {
  const failed = Object.values(draft.files).find((file) => file.kind === 'sheet' && file.rejection && draft.sheet?.fileKey !== file.key)
  if (failed) {
    return { fileKey: failed.key, name: failed.name, stats: { reason: 'parse-failed', rows: 0, matched: 0 } }
  }
  const sheet = draft.sheet
  if (!sheet || sheet.rows.length === 0) {
    return undefined
  }
  if (!hasKeyColumn(sheet.mapping)) {
    return { fileKey: sheet.fileKey, name: sheet.name, stats: { reason: 'no-key-column', rows: sheet.rows.length, matched: 0 } }
  }
  const matched = Object.keys(sheetMatchOf(draft)?.byGroup ?? {}).length
  return matched / sheet.rows.length < LOW_MATCH_SHARE
    ? { fileKey: sheet.fileKey, name: sheet.name, stats: { reason: 'low-match', rows: sheet.rows.length, matched } }
    : undefined
}

const bytes = (text: string) => new TextEncoder().encode(text).length

/** The first lines of `lines` (after a header line, if any) that fit the limit. */
const fitting = (header: string | undefined, lines: readonly string[]): number => {
  let size = header === undefined ? 0 : bytes(header) + 1
  let count = 0
  for (const line of lines) {
    size += bytes(line) + 1
    if (size > MAX_SHEET_ATTACHMENT_BYTES) {
      break
    }
    count++
  }
  return count
}

export type SupportPayload = {
  attachments: { filename: string; data: string | Uint8Array; contentType: string }[]
  /** Rows sent of all rows, when the sheet was too large to send whole. */
  trimmed?: { sent: number; total: number }
}

const trimmedCsv = (
  name: string,
  table: NonNullable<SupportRequest['table']>
): SupportPayload['attachments'][number] & { sent: number } => {
  const header = Papa.unparse({ fields: table.headers, data: [] }, { newline: '\n' }).trimEnd()
  const lines = table.rows.map((row) => Papa.unparse({ fields: table.headers, data: [row] }, { header: false, newline: '\n' }))
  const sent = fitting(header, lines)
  return { filename: `${name}.first-${sent}-rows.csv`, data: [header, ...lines.slice(0, sent)].join('\n'), contentType: 'text/csv', sent }
}

/** The attachments for a request: the file or text as it was, or its first rows when it is too large. */
export const supportPayload = async (request: SupportRequest): Promise<SupportPayload> => {
  const attachments: SupportPayload['attachments'] = []
  let trimmed: SupportPayload['trimmed']
  if (request.source.kind === 'file') {
    const { file } = request.source
    if (file.size <= MAX_SHEET_ATTACHMENT_BYTES) {
      attachments.push({
        filename: file.name,
        data: new Uint8Array(await file.arrayBuffer()),
        contentType: file.type || 'application/octet-stream'
      })
    } else if (request.table) {
      const { sent, ...csv } = trimmedCsv(file.name, request.table)
      attachments.push(csv)
      trimmed = { sent, total: request.table.rows.length }
    } else {
      throw new Error(`the file is larger than ${MAX_SHEET_ATTACHMENT_BYTES / 1_000_000} MB and could not be read to send its first rows`)
    }
  } else {
    const lines = request.source.text.split(/\r?\n/)
    if (bytes(request.source.text) <= MAX_SHEET_ATTACHMENT_BYTES) {
      attachments.push({ filename: 'pasted-rows.tsv', data: request.source.text, contentType: 'text/tab-separated-values' })
    } else {
      const [header, ...rest] = lines
      const sent = fitting(header, rest)
      attachments.push({
        filename: `pasted-rows.first-${sent}-rows.tsv`,
        data: [header, ...rest.slice(0, sent)].join('\n'),
        contentType: 'text/tab-separated-values'
      })
      trimmed = { sent, total: rest.length }
    }
  }
  attachments.push({
    filename: 'summary.json',
    data: JSON.stringify({ columns: request.columns, stats: request.stats, fileNames: request.fileNames, trimmed }, null, 2),
    contentType: 'application/json'
  })
  return { attachments, trimmed }
}

const REASON_TEXT: Record<SupportReason, string> = {
  'parse-failed': 'could not be read',
  'no-key-column': 'no column names the samples or files',
  'low-match': 'few rows match the files'
}

/** Sends the request as user feedback with the sheet attached; resolves once it left the browser. */
export const sendSheetToSupport = async (
  request: SupportRequest,
  context: { orderId?: string; user?: { name?: string; email?: string } }
): Promise<SupportPayload> => {
  const payload = await supportPayload(request)
  const { reason, rows, matched } = request.stats
  Sentry.captureFeedback(
    {
      message: `Order wizard: a sample sheet it could not use — ${request.name}: ${REASON_TEXT[reason]} (${matched} of ${rows} rows matched)`,
      name: context.user?.name,
      email: context.user?.email,
      tags: { source: 'order-wizard-unmatched-sheet', orderId: context.orderId ?? 'none' }
    },
    { attachments: payload.attachments }
  )
  if (!(await Sentry.flush(10_000))) {
    throw new Error('it did not leave the browser in time')
  }
  return payload
}
